/**
 * Stato della schermata del wizard. Tutto il percorso lo decide il server: qui si tiene solo
 * quello che serve a disegnare (card corrente, caricamento, errore, foglio di conferma).
 *
 * Tre regole di schermata:
 * - la bozza del campo libero si cancella **solo** dopo che il server ha risposto (X-03);
 * - un fatto sanitario proposto apre la conferma specifica prima di andare avanti;
 * - ogni errore mostra cosa e successo e come rimediare, e lascia riprovare.
 */
import { useCallback, useEffect, useState } from 'react'

import type { ApiError, WizardAnswerKind } from '@mamma/contracts'
import { clearDraft } from '../components'
import { ApiFailure } from '../api/client'
import type { AnswerBody, ProposedFact, SessionView, SummaryData, WizardCardData } from '../api/wizard'
import { wizardApi } from '../api/wizard'

export type Phase = 'caricamento' | 'domanda' | 'conferma' | 'riepilogo' | 'pausa' | 'chiuso' | 'errore'

export type WizardState = {
  phase: Phase
  card: WizardCardData | null
  recap: string | null
  resume: string | null
  error: ApiError | null
  pendingFact: ProposedFact | null
  summary: SummaryData | null
  busy: boolean
  /** Come lei ha scelto di farsi chiamare: da qui il nome dell'app (DEC-15). */
  displayForm: string | null
}

/** sessionStorage: prima domanda di mia da mostrare in Oggi dopo il "Sì" a WQ-B13-1. */
export const PRIMA_AZIONE_KEY = 'mia-prima-azione'

export function draftKeyFor(card: WizardCardData | null): string {
  return `wizard:${card?.id ?? 'nessuna'}`
}

export function useWizard() {
  const [state, setState] = useState<WizardState>({
    phase: 'caricamento',
    card: null,
    recap: null,
    resume: null,
    error: null,
    pendingFact: null,
    summary: null,
    busy: false,
    displayForm: null,
  })

  const fail = useCallback((problema: unknown) => {
    const error =
      problema instanceof ApiFailure
        ? problema.error
        : {
            code: 'imprevisto',
            message_it: 'Qualcosa non ha funzionato mentre salvavo la sua risposta.',
            remedy_it: 'Tocchi Riprova: quello che ha scritto è ancora nel campo.',
          }
    setState((prev) => ({ ...prev, phase: 'errore', error, busy: false }))
  }, [])

  const openSummary = useCallback(async () => {
    setState((prev) => ({ ...prev, busy: true }))
    try {
      const summary = await wizardApi.summary()
      setState((prev) => ({ ...prev, phase: 'riepilogo', summary, error: null, busy: false }))
    } catch (problema) {
      fail(problema)
    }
  }, [fail])

  const applyView = useCallback(
    (view: SessionView) => {
      setState((prev) => ({ ...prev, displayForm: view.display_form }))
      if (view.session.status === 'completato') {
        setState((prev) => ({ ...prev, phase: 'chiuso', card: null, error: null, busy: false }))
        return
      }
      if (view.next_question === null) {
        void openSummary()
        return
      }
      setState((prev) => ({
        ...prev,
        phase: view.session.status === 'in_pausa' ? 'pausa' : 'domanda',
        card: view.next_question,
        recap: view.recap_it,
        resume: view.resume_it,
        error: null,
        busy: false,
      }))
    },
    [openSummary],
  )

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, phase: 'caricamento', error: null }))
    try {
      applyView(await wizardApi.session())
    } catch (problema) {
      fail(problema)
    }
  }, [applyView, fail])

  useEffect(() => {
    void load()
  }, [load])

  const send = useCallback(
    async (body: AnswerBody, draftKey: string) => {
      setState((prev) => ({ ...prev, busy: true }))
      try {
        const result = await wizardApi.answer(body)
        clearDraft(draftKey) // solo adesso: il server ha la risposta
        setState((prev) => ({ ...prev, displayForm: result.display_form }))
        const daConfermare = result.proposed_facts.find((fatto) => fatto.needs_confirm)
        if (daConfermare) {
          setState((prev) => ({
            ...prev,
            phase: 'conferma',
            pendingFact: daConfermare,
            card: result.next_question,
            error: null,
            busy: false,
          }))
          return
        }
        if (result.next_question === null) {
          await openSummary()
          return
        }
        setState((prev) => ({
          ...prev,
          phase: result.session.status === 'in_pausa' ? 'pausa' : 'domanda',
          card: result.next_question,
          recap: null,
          resume: null,
          pendingFact: null,
          error: null,
          busy: false,
        }))
      } catch (problema) {
        fail(problema)
      }
    },
    [fail, openSummary],
  )

  const answer = useCallback(
    (card: WizardCardData, scelte: string[], testo: string) => {
      const pulito = testo.trim()
      const kind: WizardAnswerKind = scelte.length > 0 ? 'opzione' : 'testo'
      const body: AnswerBody = { question_id: card.id, answer_kind: kind }
      if (scelte.length > 0) {
        if (card.multi) body.options = scelte
        else body.option = scelte[0]
        if (pulito) body.raw_text = pulito
      } else {
        body.raw_text = pulito
      }
      void send(body, draftKeyFor(card))
    },
    [send],
  )

  const escape = useCallback(
    (card: WizardCardData, kind: WizardAnswerKind) => {
      void send({ question_id: card.id, answer_kind: kind }, draftKeyFor(card))
    },
    [send],
  )

  const decideFact = useCallback(
    async (decisione: 'conferma' | 'togli' | 'correggi') => {
      const fatto = state.pendingFact
      if (!fatto) return
      if (decisione === 'correggi') {
        setState((prev) => ({ ...prev, phase: 'domanda', pendingFact: null }))
        return
      }
      setState((prev) => ({ ...prev, busy: true }))
      try {
        if (decisione === 'conferma') await wizardApi.confirmFact(fatto.id, fatto.value_text)
        else await wizardApi.retireFact(fatto.id)
        setState((prev) => ({ ...prev, phase: 'domanda', pendingFact: null, busy: false }))
      } catch (problema) {
        fail(problema)
      }
    },
    [fail, state.pendingFact],
  )

  const pause = useCallback(async () => {
    setState((prev) => ({ ...prev, busy: true }))
    try {
      await wizardApi.pause()
      setState((prev) => ({ ...prev, phase: 'pausa', busy: false, error: null }))
    } catch (problema) {
      fail(problema)
    }
  }, [fail])

  const resume = useCallback(async () => {
    setState((prev) => ({ ...prev, busy: true }))
    try {
      applyView(await wizardApi.resume())
    } catch (problema) {
      fail(problema)
    }
  }, [applyView, fail])

  const complete = useCallback(async () => {
    setState((prev) => ({ ...prev, busy: true }))
    try {
      await wizardApi.complete()
      // DEC-21: se a WQ-B13-1 ha detto "Sì", si apre subito la chiacchierata con mia
      // sul tema scelto (Oggi legge la chiave e apre il pannello con la prima domanda).
      const primo = state.summary?.first_action
      if (primo?.accepted && primo.opening_it) {
        try {
          window.sessionStorage.setItem(PRIMA_AZIONE_KEY, primo.opening_it)
        } catch {
          /* senza storage si arriva comunque a Oggi */
        }
        window.location.assign('/oggi')
        return
      }
      setState((prev) => ({ ...prev, phase: 'chiuso', busy: false, error: null }))
    } catch (problema) {
      fail(problema)
    }
  }, [fail, state.summary])

  const decideSummaryFact = useCallback(
    async (factId: string, valueText: string, decisione: 'conferma' | 'togli') => {
      setState((prev) => ({ ...prev, busy: true }))
      try {
        if (decisione === 'conferma') await wizardApi.confirmFact(factId, valueText)
        else await wizardApi.retireFact(factId)
        await openSummary()
      } catch (problema) {
        fail(problema)
      }
    },
    [fail, openSummary],
  )

  return { state, load, answer, escape, decideFact, decideSummaryFact, pause, resume, complete, openSummary }
}
